import { toast } from 'sonner';
import { API_ENDPOINTS, HTTP_METHODS } from '../config/api';
import { BaseApiService, ApiResponse, PaginatedResponse } from './baseApi';
import { HsnCode } from './hsnApi';
import { EndgoodCategory } from './endgoodCategoryApi';

export interface Endgood {
  id: number;
  endgoodName: string;
  partNumber: string;
  sku: string;
  description: string;
  drawingNumber: string;
  drawingImagePath: string;
  productionWeight: number;
  isActive: boolean;
  createdBy: number;
  updatedBy: number;
  createdAt: string;
  updatedAt: string;
  hsn: HsnCode;
  endgoodCategory: EndgoodCategory;
}

// Simplified DTO for ID, name, and category
export interface EndgoodIdNameCategoryDto {
  id: number;
  endgoodName: string;
  categoryId: number;
  categoryName: string;
}

export interface EndgoodResponse extends PaginatedResponse<Endgood> {}

export interface EndgoodCreateRequest {
  endgoodName: string;
  partNumber: string;
  sku?: string; // Optional - auto-generated by backend
  description: string;
  drawingNumber: string;
  drawingImagePath: string;
  productionWeight: number;
  isActive: boolean;
  hsnId: number;
  endgoodCategoryId: number;
  createdBy: number;
  updatedBy: number;
}

export interface EndgoodUpdateRequest extends EndgoodCreateRequest {
  id: number;
}

/** Request for endgood cost API (effective date in yyyy-MM-dd) */
export interface EndgoodCostRequestDto {
  id: number;
  effectiveDate: string;
}

/** Response from endgood cost API */
export interface EndgoodCostResponseDto {
  cost?: number;
  unitCost?: number;
  [key: string]: unknown;
}

class EndgoodApiService extends BaseApiService {
  async getEndgoods(): Promise<Endgood[]> {
    try {
      const response = await this.makeRequest<ApiResponse<Endgood[]>>(
        API_ENDPOINTS.ENDGOODS.LIST,
        { method: HTTP_METHODS.GET }
      );
      if (response.success) {
        return response.data;
      }
      throw new Error(response.message || 'Failed to fetch endgoods');
    } catch (error) {
      // console.error('Error fetching endgoods:', error);
      toast.error('Failed to fetch endgoods');
      throw error;
    }
  }

  async getEndgoodsIdNameCategory(): Promise<ApiResponse<EndgoodIdNameCategoryDto[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<EndgoodIdNameCategoryDto[]>>(
        API_ENDPOINTS.ENDGOODS.ID_NAME_CATEGORY,
        { method: HTTP_METHODS.GET }
      );
      return response;
    } catch (error) {
      // console.error('Error fetching endgoods ID, name, and category:', error);
      toast.error('Failed to fetch endgoods');
      throw error;
    }
  }

  async getEndgoodsPaginated(page: number = 1, size: number = 20): Promise<ApiResponse<Endgood[]>> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
      });

      const response = await this.makeRequest<ApiResponse<Endgood[]>>(
        `${API_ENDPOINTS.ENDGOODS.LIST}?${params.toString()}`,
        { method: HTTP_METHODS.GET }
      );
      
      return response;
    } catch (error) {
      // console.error('Error fetching paginated endgoods:', error);
      toast.error('Failed to fetch endgoods');
      throw error;
    }
  }

  async searchEndgoodsPaginated(searchTerm: string, page: number = 1, size: number = 20): Promise<ApiResponse<Endgood[]>> {
    try {
      const params = new URLSearchParams({
        q: searchTerm,
        page: page.toString(),
        size: size.toString(),
      });

      const response = await this.makeRequest<ApiResponse<Endgood[]>>(
        `${API_ENDPOINTS.ENDGOODS.LIST}/search?${params.toString()}`,
        { method: HTTP_METHODS.GET }
      );
      
      return response;
    } catch (error) {
      // console.error('Error searching paginated endgoods:', error);
      toast.error('Failed to search endgoods');
      throw error;
    }
  }

  async getEndgoodById(id: string): Promise<Endgood> {
    try {
      const response = await this.makeRequest<ApiResponse<Endgood>>(
        API_ENDPOINTS.ENDGOODS.DETAIL(id),
        { method: HTTP_METHODS.GET }
      );
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch endgood');
      }
    } catch (error) {
      // console.error('Error fetching endgood:', error);
      toast.error('Failed to fetch endgood details');
      throw error;
    }
  }

  async createEndgood(endgoodData: EndgoodCreateRequest): Promise<Endgood> {
    try {
      const response = await this.makeRequest<ApiResponse<Endgood>>(
        API_ENDPOINTS.ENDGOODS.CREATE,
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify(endgoodData)
        }
      );
      
      if (response.success) {
        toast.success('Endgood created successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to create endgood');
      }
    } catch (error) {
      // console.error('Error creating endgood:', error);
      toast.error('Failed to create endgood');
      throw error;
    }
  }

  async updateEndgood(id: string, endgoodData: EndgoodUpdateRequest): Promise<Endgood> {
    try {
      const response = await this.makeRequest<ApiResponse<Endgood>>(
        API_ENDPOINTS.ENDGOODS.UPDATE(id),
        {
          method: HTTP_METHODS.PUT,
          body: JSON.stringify(endgoodData)
        }
      );
      
      if (response.success) {
        toast.success('Endgood updated successfully');
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to update endgood');
      }
    } catch (error) {
      // console.error('Error updating endgood:', error);
      toast.error('Failed to update endgood');
      throw error;
    }
  }

  async deleteEndgood(id: string): Promise<void> {
    try {
      const response = await this.makeRequest<ApiResponse<void>>(
        API_ENDPOINTS.ENDGOODS.DELETE(id),
        { method: HTTP_METHODS.DELETE }
      );
      
      if (response.success) {
        toast.success('Endgood deleted successfully');
      } else {
        throw new Error(response.message || 'Failed to delete endgood');
      }
    } catch (error) {
      // console.error('Error deleting endgood:', error);
      toast.error('Failed to delete endgood');
      throw error;
    }
  }

  async searchEndgoods(searchTerm: string): Promise<Endgood[]> {
    try {
      const response = await this.makeRequest<ApiResponse<Endgood[]>>(
        `${API_ENDPOINTS.ENDGOODS.LIST}/search?q=${encodeURIComponent(searchTerm)}`,
        { method: HTTP_METHODS.GET }
      );
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to search endgoods');
      }
    } catch (error) {
      // console.error('Error searching endgoods:', error);
      toast.error('Failed to search endgoods');
      throw error;
    }
  }

  async getEndgoodsIdName(): Promise<ApiResponse<Endgood[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<Endgood[]>>(
        API_ENDPOINTS.ENDGOODS.ID_NAME,
        { method: HTTP_METHODS.GET }
      );
      
      return response;
    } catch (error) {
      // console.error('Error fetching endgoods id-name:', error);
      toast.error('Failed to fetch endgoods');
      throw error;
    }
  }

  async getEndgoodsByCategory(categoryId: string): Promise<ApiResponse<Endgood[]>> {
    try {
      const response = await this.makeRequest<ApiResponse<Endgood[]>>(
        API_ENDPOINTS.ENDGOODS.BY_CATEGORY(categoryId),
        { method: HTTP_METHODS.GET }
      );
      
      return response;
    } catch (error) {
      // console.error('Error fetching endgoods by category:', error);
      toast.error('Failed to fetch endgoods by category');
      throw error;
    }
  }

  /** Get endgood cost for a given effective date (POST /inventory/endgoods/cost) */
  async getEndgoodCost(request: EndgoodCostRequestDto): Promise<ApiResponse<EndgoodCostResponseDto>> {
    try {
      const response = await this.makeRequest<ApiResponse<EndgoodCostResponseDto>>(
        API_ENDPOINTS.ENDGOODS.COST,
        {
          method: HTTP_METHODS.POST,
          body: JSON.stringify({
            id: request.id,
            effectiveDate: request.effectiveDate,
          }),
        }
      );
      return response;
    } catch (error) {
      toast.error('Failed to fetch endgood cost');
      throw error;
    }
  }
}

export const endgoodApiService = new EndgoodApiService();
